import React from "react";
import { useAuth } from "@/store/auth";
import { can, decodeJwt } from "@/lib/rbac";

// A no-nonsense diagnostic page. Visit /whoami to see EXACTLY what the
// browser thinks the session is. Useful when the sidebar hides something
// the user expects to see (e.g. Users menu).
//
// This page is intentionally accessible to any logged-in user — no admin
// gate — so operators / viewers can grab a dump to hand back to support.
export default function WhoAmI() {
  const token = useAuth((s) => s.token);
  const username = useAuth((s) => s.username);
  const role = useAuth((s) => s.role);
  const clientIds = useAuth((s) => s.clientIds);
  const expires_at = useAuth((s) => s.expires_at);

  const payload = decodeJwt(token);

  // Snapshot the exact capability matrix decisions for THIS session so users
  // don't have to guess whether their role is spelt right.
  const CAPS = [
    "read:users","write:users",
    "read:clients","write:clients",
    "read:contacts","write:contacts",
    "read:devices","write:devices",
    "read:gateways","write:gateways",
    "read:callbacks","write:callbacks",
    "read:envelope","write:envelope",
    "read:mqtt","write:mqtt",
    "read:outputs","write:outputs",
    "read:pipelines","write:pipelines",
  ];

  const dump = {
    build: {
      // BUILD_TS is stamped in at build time via craco.config.js (see
      // `webpack.DefinePlugin`). If it's undefined, the build is very old.
      timestamp: (typeof process !== "undefined" && process.env.REACT_APP_BUILD_TS) || "unknown",
      // A tiny sentinel that only exists in RBAC-enabled builds. If false,
      // the JS bundle is pre-Users-menu (June 2026 or older).
      hasUsersRoute: CAPS.includes("read:users"),
    },
    authStore: { username, role, clientIds, expires_at, tokenLen: token?.length || 0 },
    jwtPayload: payload,
    capabilities: Object.fromEntries(CAPS.map((c) => [c, can(role, c)])),
  };

  const json = JSON.stringify(dump, null, 2);

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto font-mono">
      <h1 className="font-heading text-2xl font-bold mb-1">WhoAmI</h1>
      <p className="text-sm text-zinc-500 mb-4">
        Diagnostic dump — copy this and paste it back to whoever helps you.
      </p>
      <div className="flex gap-2 mb-3">
        <button
          data-testid="whoami-copy"
          onClick={() => {
            navigator.clipboard.writeText(json);
          }}
          className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900"
        >
          Copy to clipboard
        </button>
        <a
          href="/"
          className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50 no-underline text-zinc-800"
        >
          Back to dashboard
        </a>
      </div>
      <pre
        data-testid="whoami-json"
        className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all"
      >
        {json}
      </pre>
    </div>
  );
}
